import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { TrendingUp, TrendingDown, Sparkles } from "lucide-react";

/**
 * Net-Benefit widget for a single card (Card Detail → Benefits tab).
 * Reads /api/analytics/reward-value/{cardId} and renders:
 *   Rewards Earned − Annual Fees Paid = Net Benefit (color-coded)
 *   + break-even progress bar
 */
export default function RewardValueCard({ cardId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get(`/analytics/reward-value/${cardId}`)
            .then((r) => { if (alive) setData(r.data); })
            .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Could not load rewards"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [cardId]);

    if (loading) {
        return (
            <div className="form-section animate-pulse" data-testid="reward-value-loading">
                <div className="h-4 w-32 bg-stone-200 rounded mb-3" />
                <div className="h-8 w-48 bg-stone-200 rounded" />
            </div>
        );
    }
    if (err || !data) {
        return null; // fail-quiet — widget is additive
    }

    const net = data.net_benefit;
    const rewards = data.rewards_earned_ytd;
    const fees = data.annual_fee_paid;
    const breakEven = data.break_even_at || 0;
    const progressPct = breakEven > 0 ? Math.min(100, Math.round((rewards / breakEven) * 100)) : (rewards > 0 ? 100 : 0);
    const isPositive = net >= 0;

    return (
        <div
            className="form-section mb-4 border-l-4 border-stone-900"
            data-testid="reward-value-widget"
            style={{ borderLeftColor: isPositive ? "#047857" : "#b91c1c" }}
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                    <div className="eyebrow flex items-center gap-1.5">
                        <Sparkles size={11} /> Net Card Benefit · {data.fy_label}
                    </div>
                    <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                        <span
                            className={`font-display text-3xl sm:text-4xl tabular-nums ${isPositive ? "text-emerald-700" : "text-red-700"}`}
                            data-testid="reward-value-net"
                        >
                            {isPositive ? "+" : "−"}{fmtMoney(Math.abs(net))}
                        </span>
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${isPositive ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-red-700 border-red-200 bg-red-50"}`}>
                            {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                            {isPositive ? "Card is paying for itself" : "Below break-even"}
                        </span>
                    </div>
                    <div className="text-xs text-stone-500 mt-2 tabular-nums">
                        Earned <span className="text-stone-900 font-medium">{fmtMoney(rewards)}</span>
                        {" "}− Fees <span className="text-stone-900 font-medium">{fmtMoney(fees)}</span>
                        {breakEven > 0 && (
                            <> · Break-even at <span className="text-stone-900 font-medium">{fmtMoney(breakEven)}</span> in rewards</>
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-6 text-xs">
                    <div>
                        <div className="eyebrow">Cashback</div>
                        <div className="font-mono-figure tabular-nums text-stone-900 mt-1">{fmtMoney(data.cashback)}</div>
                    </div>
                    <div>
                        <div className="eyebrow">Points value</div>
                        <div className="font-mono-figure tabular-nums text-stone-900 mt-1">{fmtMoney(data.reward_points_value)}</div>
                        {data.reward_points_earned > 0 && (
                            <div className="text-[10px] text-stone-400 tabular-nums">
                                {data.reward_points_earned.toLocaleString()} pts × ₹{data.point_value}
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="eyebrow">FY spend</div>
                        <div className="font-mono-figure tabular-nums text-stone-900 mt-1">{fmtMoney(data.total_spend)}</div>
                        <div className="text-[10px] text-stone-400 tabular-nums">{data.expense_count} txns</div>
                    </div>
                </div>
            </div>

            {breakEven > 0 && (
                <div className="mt-4">
                    <div className="w-full h-1.5 bg-stone-100 rounded-full overflow-hidden">
                        <div
                            className={`h-full transition-all duration-500 ${isPositive ? "bg-emerald-500" : "bg-amber-500"}`}
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                    <div className="text-[10.5px] text-stone-500 mt-1.5 tabular-nums">
                        {progressPct}% of the way to break-even
                        {data.annual_fee_waived && <span className="ml-2 text-emerald-700">· Fee waived this cycle</span>}
                    </div>
                </div>
            )}

            {/* Bill loyalty slice — how much of this card's spend went to recurring bills */}
            {data.bill_payments_count > 0 && (
                <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between gap-3 flex-wrap" data-testid="reward-value-bill-slice">
                    <div>
                        <div className="eyebrow flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-blue-500" /> Recurring bill payments
                        </div>
                        <div className="text-sm text-stone-700 mt-1">
                            <span className="font-medium text-stone-900 tabular-nums">{fmtMoney(data.bill_payments_amount)}</span>
                            <span className="text-stone-500"> · {data.bill_payments_count} payment{data.bill_payments_count === 1 ? "" : "s"}</span>
                            <span className="text-stone-500"> · {data.bill_payments_share_pct}% of FY spend</span>
                        </div>
                    </div>
                    <Link to="/bills" className="text-xs text-blue-700 hover:underline">View bills →</Link>
                </div>
            )}
        </div>
    );
}
